import type {CurriedSelector, Reducer} from '@/redux/types';
import type {State} from '@/redux/typesNew';
import type {Availability, VacancyStatus} from '@/redux/v1/availability/models';
import type {
  AlternativeFlightOffer,
  Data,
  OfferAlternativeFlightsState,
  Query,
  Status,
} from '@/redux/v1/offerAlternativeFlights/models';
import type {ActionHandlers} from '@travel-frontend/state-handling-sdk';

import {
  createReducerUsingActionHandlers,
  ERROR,
  PENDING,
  SUCCESS,
} from '@travel-frontend/state-handling-sdk';
import {curry, keyBy} from 'lodash';

import {
  selectAvailability,
  selectVacancyStatus,
} from '@/redux/v1/availability/reducer';
import {FETCH_OFFER_ALTERNATIVE_FLIGHTS} from '@/redux/v1/offerAlternativeFlights/models';
import {makeCacheKey} from '@/redux/v1/offerAlternativeFlights/utils';
import {offerAlternativeFlightsState} from '@/redux/v1/v1State';

const EMPTY_DATA: Data = [];

const initialState: OfferAlternativeFlightsState = {
  status: {},
  byQuery: {},
  byOfferId: {},
  errors: {},
};

const actionHandlers: ActionHandlers<OfferAlternativeFlightsState> = {
  [PENDING(FETCH_OFFER_ALTERNATIVE_FLIGHTS)]: (
    state: OfferAlternativeFlightsState,
    action
  ) => ({
    ...state,
    status: {
      ...state.status,
      [action.meta.id]: 'loading',
    },
  }),
  [SUCCESS(FETCH_OFFER_ALTERNATIVE_FLIGHTS)]: (
    state: OfferAlternativeFlightsState,
    action
  ) => ({
    ...state,
    status: {
      ...state.status,
      [action.meta.id]: 'successful',
    },
    byQuery: {
      ...state.byQuery,
      [action.meta.id]: action.payload,
    },
    byOfferId: {
      ...state.byOfferId,
      ...keyBy(action.payload, 'id'),
    },
  }),
  [ERROR(FETCH_OFFER_ALTERNATIVE_FLIGHTS)]: (
    state: OfferAlternativeFlightsState,
    action
  ) => ({
    ...state,
    status: {
      ...state.status,
      [action.meta.id]: 'failed',
    },
    errors: {
      ...state.errors,
      [action.meta.id]: action.error,
    },
  }),
};

export const reducer: Reducer<OfferAlternativeFlightsState> =
  createReducerUsingActionHandlers<any>(initialState, actionHandlers);

export const selectStatus: CurriedSelector<Query, Status> = curry(
  (query: Query, state: State) =>
    offerAlternativeFlightsState(state).status[makeCacheKey(query)] || 'idle'
);

export const selectData: CurriedSelector<Query, Data> = curry(
  (query: Query, state: State) =>
    offerAlternativeFlightsState(state).byQuery[makeCacheKey(query)] ??
    EMPTY_DATA
);

export const selectError: CurriedSelector<Query, null | Error> = curry(
  (query: Query, state: State) =>
    offerAlternativeFlightsState(state).errors[makeCacheKey(query)] ?? null
);

export const selectAlternativeFlightOffer: CurriedSelector<
  string,
  AlternativeFlightOffer | null
> = curry(
  (offerId: string, state: State) =>
    offerAlternativeFlightsState(state).byOfferId[offerId] ?? null
);

export const selectAlternativeFlightsOffersVacancyStatus: CurriedSelector<
  Query,
  Record<string, VacancyStatus>
> = curry((query: Query, state: State) => {
  const alternativeFlightsOffers = selectData(query, state);

  return Object.fromEntries(
    alternativeFlightsOffers.map((alternativeFlightOffer) => [
      alternativeFlightOffer.id,
      selectVacancyStatus(alternativeFlightOffer.id, state),
    ])
  );
});

export const selectAvailableAlternativeFlightsOffers: CurriedSelector<
  Query,
  Record<string, Availability | null>
> = curry((query: Query, state: State) => {
  const alternativeFlightsOffers = selectData(query, state);

  return Object.fromEntries(
    alternativeFlightsOffers.map((alternativeFlightOffer) => [
      alternativeFlightOffer.id,
      selectAvailability(alternativeFlightOffer.id, state) ?? null,
    ])
  );
});
